import { useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField } from "../FloatingField";
import { useStore } from "../../store/globalState";
import { payoutClaimBanks } from "../../gallery/payoutClaimMock";
import { payerCodesEquivalent } from "../../utils/payerCode";
import type { IBankAccountDetails } from "../../interfaces/payment";
import type { EstimateOption } from "../../interfaces/payout";
import {
  bankPrefixMismatch,
  inspectBankAccountNumber,
} from "../../utils/bankAccountNumber";
import { BackArrowLight, CloseLight } from "../../assets/navIcons";

const ACCOUNT_LENGTH: Record<string, number> = { MX: 18, AR: 22, PE: 20 };
const MAX_ACCOUNT_NUMBER_LENGTH = 34;

const MONO_INPUT: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  letterSpacing: "0.04em",
};

const sanitizeAccountNumber = (raw: string, exactLength?: number): string => {
  if (exactLength != null) {
    return raw.replace(/\D/g, "").slice(0, exactLength);
  }
  return raw
    .replace(/\s/g, "")
    .replace(/[^a-z0-9]/gi, "")
    .toUpperCase()
    .slice(0, MAX_ACCOUNT_NUMBER_LENGTH);
};

export interface BankAccountSelection extends IBankAccountDetails {
  option: EstimateOption | null;
}

export interface BankAccountDetailsSlotProps {
  destinationCountry: string;
  options: EstimateOption[];
  value?: IBankAccountDetails | null;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (result: BankAccountSelection) => void;
}

/**
 * Gallery-safe bank account details for payout-claim (static banks). Form-only
 * alta — saved accounts live on the first payout popup. No network.
 */
export const BankAccountDetailsSlot: React.FC<BankAccountDetailsSlotProps> = ({
  destinationCountry,
  options,
  value,
  onBack,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation("payout-claim");
  const { theme } = useStore();
  const { bodyCopy, backgroundBtn, textColorBtn, errorColor } = theme.whiteView;
  const fieldColors = {
    background: theme.whiteView.background,
    title: theme.whiteView.title,
    bodyCopy,
    inactiveColor: theme.whiteView.subtitile,
    errorColor: theme.whiteView.errorColor,
  };

  const methodOptions = useMemo(
    () => options.filter((o) => o.payoutMethod === "BANK_ACCOUNT"),
    [options],
  );

  const banks = useMemo(() => {
    const offerable = new Set(
      methodOptions.map((o) => o.payerCode).filter(Boolean) as string[],
    );
    if (offerable.size === 0) return payoutClaimBanks;
    return payoutClaimBanks.filter((b) =>
      [...offerable].some((code) => payerCodesEquivalent(code, b.id)),
    );
  }, [methodOptions]);

  const bankLabel = (code: string): string =>
    banks.find((b) => b.id === code)?.name ?? code;

  const resolveOption = (
    bankId: string | undefined | null,
  ): EstimateOption | null => {
    if (!bankId) return null;
    return (
      methodOptions.find((o) => payerCodesEquivalent(o.payerCode, bankId)) ??
      null
    );
  };

  const [bankId, setBankId] = useState<string | null>(value?.bankId ?? null);
  const [accountNumber, setAccountNumber] = useState(
    value?.source === "new" ? (value.accountNumber ?? "") : "",
  );
  const expectedLength = ACCOUNT_LENGTH[destinationCountry.toUpperCase()];
  const identifier = sanitizeAccountNumber(accountNumber, expectedLength);
  const inspection = inspectBankAccountNumber(destinationCountry, identifier);
  const selectedPrefixes = banks.find((b) => b.id === bankId)?.accountPrefixes;
  const mismatch =
    inspection.canContinue &&
    bankPrefixMismatch(inspection.bankCode, selectedPrefixes);
  const valid =
    Boolean(bankId) &&
    inspection.canContinue &&
    banks.some((b) => b.id === bankId);

  return (
    <section className="payout-details-slot">
      <header className="payout-details-header">
        <button
          type="button"
          className="payout-header-action payout-back-btn"
          aria-label={t("common.back")}
          onClick={onBack}
        >
          <BackArrowLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
        <Title
          title={t("payout.details.BANK_ACCOUNT.title")}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>

      <form
        className="payout-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid || !bankId) return;
          onSubmit({
            source: "new",
            bankId,
            bankName: bankLabel(bankId),
            accountNumber: identifier,
            last4: identifier.slice(-4),
            option: resolveOption(bankId),
          });
        }}
      >
        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.bank")}
            value={bankId ?? ""}
            colors={fieldColors}
          >
            <select
              style={{ color: bodyCopy, cursor: "pointer" }}
              value={bankId ?? ""}
              onChange={(e) => {
                setBankId(e.target.value || null);
              }}
            >
              <option value="" />
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </FloatingField>
          {banks.length === 0 && (
            <span className="payout-hint">{t("payout.fields.noBanks")}</span>
          )}
        </div>

        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.accountNumber")}
            value={identifier}
            colors={fieldColors}
          >
            <input
              type="text"
              inputMode={expectedLength == null ? "text" : "numeric"}
              maxLength={expectedLength ?? MAX_ACCOUNT_NUMBER_LENGTH}
              autoComplete="off"
              style={{ color: bodyCopy, ...MONO_INPUT }}
              value={identifier}
              onChange={(e) => {
                setAccountNumber(
                  sanitizeAccountNumber(e.target.value, expectedLength),
                );
              }}
            />
          </FloatingField>
          {expectedLength != null && (
            <span className="payout-hint">
              {identifier.length} / {expectedLength}
            </span>
          )}
          {identifier.length > 0 && !inspection.formatOk && (
            <span className="payout-hint" style={{ color: errorColor }}>
              {t("payout.fields.accountNumberInvalid")}
            </span>
          )}
          {mismatch && (
            <span className="payout-hint" style={{ color: errorColor }}>
              {t("payout.fields.bankMismatch")}
            </span>
          )}
        </div>

        <Button
          type="submit"
          size="large"
          disabled={!valid}
          background={backgroundBtn}
          color={textColorBtn}
        >
          {mismatch
            ? t("payout.fields.bankMismatchConfirm")
            : t("common.continue")}
        </Button>
      </form>
    </section>
  );
};
